import { cpFetch, createAuthHeaders, getDefaultTenantId, isProductionHost } from '../../lib/cpClient';
import { FEATURES } from '../../lib/features';
import { useAuthStore } from '../../store/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appendAppIdQueryParam, appendTemplateScopeParams, useSelectedAppId } from '@/hooks/useSelectedAppId';
import { GraphData, GraphScope, PlanResponse, PlanningMode } from './types';
import {
  honestRelationshipTenant,
  normalizeTemplateSchemaGraph,
  relationshipAuthHeaders,
  type TwinRecordSources,
} from './twinChain';

function resolveRelationshipTenant(selectedTenant?: string | null): string | null {
  return honestRelationshipTenant({
    selectedTenant,
    hostedTenant: getDefaultTenantId(),
    environment: isProductionHost() ? 'prod' : getDefaultTenantId(),
  });
}

function relationshipHeaders(token: string, selectedTenant?: string | null): Record<string, string> {
  const tenant = resolveRelationshipTenant(selectedTenant);
  if (tenant) {
    return { ...createAuthHeaders(token, tenant) };
  }
  return relationshipAuthHeaders(token, null);
}

// API client functions
export const relationshipsApi = {
  // Get graph slice
  getGraph: async (scope: GraphScope, token: string, tenant: string | null, appId?: string | null): Promise<GraphData> => {
    const params = new URLSearchParams({
      scope: scope.scope,
      ...(scope.rootId && { rootId: scope.rootId }),
      ...(scope.depth && { depth: scope.depth.toString() }),
      ...(scope.filters?.kinds && { kinds: scope.filters.kinds.join(',') }),
      ...(scope.filters?.relations && { relations: scope.filters.relations.join(',') })
    });
    appendAppIdQueryParam(params, appId || null);

    return cpFetch(`relationships/graph?${params}`, {
      headers: relationshipHeaders(token, tenant)
    });
  },

  // Sync relationships into canonical store
  syncRelationships: async (
    options: { appId?: string | null; dryRun?: boolean; limits?: Record<string, number> },
    token: string,
    tenant: string
  ) => {
    return cpFetch('relationships/sync', {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        app_id: options.appId || null,
        dry_run: options.dryRun || false,
        limits: options.limits || {}
      })
    });
  },

  // Seed a demo graph for the relationship manager
  seedRelationships: async (options: { appId?: string | null }, token: string, tenant: string | null) => {
    return cpFetch('relationships/seed', {
      method: 'POST',
      headers: {
        ...relationshipHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        app_id: options.appId || null
      })
    });
  },

  // Generate plan from graph changes
  generatePlan: async (
    observedGraph: GraphData,
    desiredGraph: GraphData,
    mode: PlanningMode,
    token: string,
    tenant: string
  ): Promise<PlanResponse> => {
    return cpFetch('relationships/plan', {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        observedGraph,
        desiredGraph,
        mode
      })
    });
  },

  // Dry run ChangeSet
  dryRunChangeSet: async (changeSet: any, token: string, tenant: string) => {
    return cpFetch('changesets/dry-run', {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(changeSet)
    });
  },

  // Propose ChangeSet
  proposeChangeSet: async (changeSet: any, rationale: string, appId: string | null, token: string, tenant: string) => {
    return cpFetch('relationships/changesets', {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        draftChangeSet: changeSet,
        rationale,
        app_id: appId
      })
    });
  },

  // Create node
  createNode: async (kind: string, label: string, props: any, token: string, tenant: string) => {
    return cpFetch('relationships/node', {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        kind,
        label,
        props,
        tenant_id: tenant
      })
    });
  },

  // Create edge
  createEdge: async (from: string, to: string, type: string, props: any, token: string, tenant: string | null) => {
    const honestTenant = resolveRelationshipTenant(tenant);
    return cpFetch('relationships/edge', {
      method: 'POST',
      headers: {
        ...relationshipHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        from,
        to,
        type,
        props,
        ...(honestTenant ? { tenant_id: honestTenant } : {})
      })
    });
  },

  // Update edge metadata
  updateEdge: async (edgeId: string, metadata: Record<string, any>, token: string, tenant: string) => {
    return cpFetch(`relationships/edge/${encodeURIComponent(edgeId)}`, {
      method: 'PATCH',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ metadata })
    });
  },

  // Update node metadata
  updateNode: async (
    payload: { entity_type: string; entity_id: string; label?: string; tags?: string[]; props?: Record<string, any> },
    token: string,
    tenant: string
  ) => {
    return cpFetch('relationships/node', {
      method: 'PATCH',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
  },

  // Delete edge
  deleteEdge: async (edgeId: string, token: string, tenant: string) => {
    return cpFetch(`relationships/edge/${edgeId}`, {
      method: 'DELETE',
      headers: createAuthHeaders(token, tenant)
    });
  },

  // Delete node
  deleteNode: async (nodeId: string, token: string, tenant: string) => {
    return cpFetch(`relationships/node/${nodeId}`, {
      method: 'DELETE',
      headers: createAuthHeaders(token, tenant)
    });
  },

  // Get AI suggestions
  getSuggestions: async (token: string, tenant: string, appId?: string | null) => {
    const params = new URLSearchParams();
    appendAppIdQueryParam(params, appId || null);
    const qs = params.toString();
    return cpFetch(`relationships/suggestions${qs ? `?${qs}` : ''}`, {
      headers: createAuthHeaders(token, tenant)
    });
  },

  // Auto-heal relationships
  autoHeal: async (token: string, tenant: string) => {
    return cpFetch('relationships/auto-heal', {
      method: 'POST',
      headers: {
        ...createAuthHeaders(token, tenant),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });
  },

  // Get template schema graph (from contextualizer MongoDB)
  getTemplateSchema: async (token: string, tenant: string | null, appId?: string | null): Promise<GraphData> => {
    const params = new URLSearchParams();
    appendAppIdQueryParam(params, appId || null);
    const qs = params.toString();
    const raw = await cpFetch(`relationships/template-schema${qs ? `?${qs}` : ''}`, {
      headers: relationshipHeaders(token, tenant)
    });
    return normalizeTemplateSchemaGraph(raw);
  },
};

// React Query hooks

/** Standards-plane template/IKU/relationship-type schema graph */
export function useTemplateSchemaGraph() {
  const { token, selectedTenant } = useAuthStore();
  const appId = useSelectedAppId();

  return useQuery({
    queryKey: ['rels', 'template-schema', selectedTenant, appId],
    queryFn: () => {
      if (!token) throw new Error('No authentication token');
      return relationshipsApi.getTemplateSchema(token, resolveRelationshipTenant(selectedTenant), appId);
    },
    enabled: !!token && !!appId,
    staleTime: 30000,
  });
}

/** Platform instance relationships (platform_relationships collection) */
export function useInstanceGraphData(scope: GraphScope, enabled = true) {
  const { token, selectedTenant } = useAuthStore();
  const appId = useSelectedAppId();

  return useQuery({
    queryKey: ['rels', 'graph', 'instances', { scope, tid: selectedTenant, appId }],
    queryFn: () => {
      if (!token) throw new Error('No authentication token');
      return relationshipsApi.getGraph(scope, token, resolveRelationshipTenant(selectedTenant), appId);
    },
    enabled: !!token && !!appId && enabled,
    staleTime: 5000,
    refetchInterval: 10000
  });
}

/** Entity registry + IoT + PIM reads for the twin chain. Failures stay honest-empty. */
export function useTwinRecordSources() {
  const { token, selectedTenant } = useAuthStore();
  const appId = useSelectedAppId();

  return useQuery({
    queryKey: ['rels', 'twin-records', selectedTenant, appId],
    queryFn: async (): Promise<TwinRecordSources> => {
      if (!token) throw new Error('No authentication token');
      const headers = relationshipHeaders(token, selectedTenant);
      const empty: TwinRecordSources = { entities: [], devices: [], products: [] };
      const [entitiesRes, devicesRes, productsRes] = await Promise.all([
        cpFetch('entity/entities?limit=100', { headers }).catch(() => null),
        cpFetch('iot/devices', { headers }).catch(() => null),
        cpFetch('pim/products?limit=50', { headers }).catch(() => null),
      ]);
      const entities = entitiesRes?.data ?? entitiesRes?.entities ?? [];
      const devices = Array.isArray(devicesRes) ? devicesRes : (devicesRes?.items ?? devicesRes?.devices ?? []);
      const products = productsRes?.items ?? productsRes?.data ?? productsRes?.products ?? [];
      return {
        entities: Array.isArray(entities) ? entities : empty.entities,
        devices: Array.isArray(devices) ? devices : empty.devices,
        products: Array.isArray(products) ? products : empty.products,
      };
    },
    enabled: !!token && !!appId,
    staleTime: 15000,
  });
}

/** @deprecated Use useTemplateSchemaGraph (standards) or useInstanceGraphData (instances) */
export function useGraphData(scope: GraphScope) {
  return useInstanceGraphData(scope, true);
}

/** Fetches instance relationship graph (platform_relationships) for infra/combined modes */
export function useInfrastructureGraphData(enabled = false) {
  const { token, selectedTenant } = useAuthStore();
  const appId = useSelectedAppId();

  return useQuery({
    queryKey: ['rels', 'graph', 'infrastructure', selectedTenant, appId],
    queryFn: async (): Promise<GraphData> => {
      if (!token) throw new Error('No authentication token');
      const params = new URLSearchParams({ scope: 'tenant' });
      appendAppIdQueryParam(params, appId);
      const headers = relationshipHeaders(token, selectedTenant);
      return cpFetch(`relationships/graph?${params}`, { headers });
    },
    enabled: !!token && !!appId && enabled,
    staleTime: 30000,
  });
}

export function usePlanGeneration() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rels', 'plan'],
    mutationFn: async ({ observedGraph, desiredGraph, mode }: {
      observedGraph: GraphData;
      desiredGraph: GraphData;
      mode: PlanningMode;
    }) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication token or tenant');
      }
      return relationshipsApi.generatePlan(observedGraph, desiredGraph, mode, token, selectedTenant);
    },
    onSuccess: () => {
      // Invalidate graph data to refresh
      queryClient.invalidateQueries({ queryKey: ['rels', 'graph'] });
    }
  });
}

export function useRelationshipSync() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rels', 'sync'],
    mutationFn: async (options: { appId?: string | null; dryRun?: boolean; limits?: Record<string, number> }) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication token or tenant');
      }
      return relationshipsApi.syncRelationships(options, token, selectedTenant);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rels', 'graph'] });
      queryClient.invalidateQueries({ queryKey: ['rels', 'suggestions'] });
    }
  });
}

export function useSeedRelationships() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rels', 'seed'],
    mutationFn: async (options: { appId?: string | null }) => {
      if (!token) {
        throw new Error('No authentication token');
      }
      return relationshipsApi.seedRelationships(options, token, resolveRelationshipTenant(selectedTenant));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rels', 'graph'] });
    }
  });
}

export function useChangeSetProposal() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ changeSet, rationale, appId }: { changeSet: any; rationale: string; appId: string | null }) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication token or tenant');
      }
      return relationshipsApi.proposeChangeSet(changeSet, rationale, appId, token, selectedTenant);
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['rels'] });
      queryClient.invalidateQueries({ queryKey: ['changesets'] });
    }
  });
}

export function useUpdateNodeMetadata() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rels', 'node-meta'],
    mutationFn: async (payload: { entity_type: string; entity_id: string; label?: string; tags?: string[]; props?: Record<string, any> }) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication token or tenant');
      }
      return relationshipsApi.updateNode(payload, token, selectedTenant);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rels', 'graph'] });
    }
  });
}

export function useUpdateEdgeMetadata() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rels', 'edge-meta'],
    mutationFn: async (payload: { edgeId: string; metadata: Record<string, any> }) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication token or tenant');
      }
      return relationshipsApi.updateEdge(payload.edgeId, payload.metadata, token, selectedTenant);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rels', 'graph'] });
    }
  });
}

export function useDeleteEdge() {
  const { token, selectedTenant } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['rels', 'edge-delete'],
    mutationFn: async (edgeId: string) => {
      if (!token || !selectedTenant) {
        throw new Error('No authentication token or tenant');
      }
      return relationshipsApi.deleteEdge(edgeId, token, selectedTenant);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rels', 'graph'] });
    }
  });
}

// Domain detail API hooks (reused from other features)
export function useApps() {
  const { token, selectedTenant } = useAuthStore();
  const appId = useSelectedAppId();

  return useQuery({
    queryKey: ['rels', 'domain', 'apps', selectedTenant, appId],
    queryFn: async () => {
      if (!token || !selectedTenant) throw new Error('No auth');
      if (!FEATURES.resourcesApps) {
        return [];
      }
      const response = await cpFetch('resources/apps', {
        headers: createAuthHeaders(token, selectedTenant)
      });
      if (!response.ok) throw new Error(`Failed to fetch apps: ${response.status}`);
      return response.json();
    },
    enabled: FEATURES.resourcesApps && !!token && !!selectedTenant && !!appId
  });
}

export function useDevices() {
  const { token, selectedTenant } = useAuthStore();
  const appId = useSelectedAppId();

  return useQuery({
    queryKey: ['rels', 'domain', 'devices', selectedTenant, appId],
    queryFn: async () => {
      if (!token || !selectedTenant) throw new Error('No auth');
      const response = await cpFetch('iot/devices', {
        headers: createAuthHeaders(token, selectedTenant)
      });
      if (!response.ok) throw new Error(`Failed to fetch devices: ${response.status}`);
      return response.json();
    },
    enabled: !!token && !!selectedTenant && !!appId
  });
}

export function useTemplates() {
  const { token, selectedTenant } = useAuthStore();
  const appId = useSelectedAppId();

  return useQuery({
    queryKey: ['rels', 'domain', 'templates', selectedTenant, appId],
    queryFn: async () => {
      if (!token || !selectedTenant) throw new Error('No auth');
      if (!appId) return [];
      if (!FEATURES.contextTemplates) {
        return [];
      }
      const params = new URLSearchParams({
        catalogState: 'all',
        sortBy: 'name',
      });
      appendTemplateScopeParams(params, appId, 'used');
      const response = await cpFetch(`api/templates?${params.toString()}`, {
        headers: createAuthHeaders(token, selectedTenant)
      });
      return Array.isArray(response?.templates) ? response.templates : [];
    },
    enabled: FEATURES.contextTemplates && !!token && !!selectedTenant && !!appId
  });
}

